import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./Task.scss";
import {
  getTasksAssignedByUser,
  getTasksAssignedToUser,
  updateTaskCompletion,
  updateTask,
  deleteTaskById,
} from "../../services/api/TaskApi.js";
import { getUserData, getUserDataByID } from "../../services/api/AuthApi.js";
import { getLawyers } from "../../services/api/LawyersApi.js";
import {
  BiCheck,
  BiEdit,
  BiTrash,
  BiX,
  BiCalendar,
  BiTime,
  BiUser,
  BiHash,
  BiDetail,
  BiLink,
} from "react-icons/bi";
import ConfirmationModal from "../../components/Modal/ConfirmModal.js";
import { useLoading } from "../../context/LoadingContext.js";
import { useNavigate } from "react-router-dom";

import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";

import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal.js";
import { useSearchResults } from "../../context/SearchResultsContext.js";
import { getBothCourtCasesDetailsByCourtRef } from "../../services/api/CasesApi.js";
import { WrittenFormat } from "../../components/WrittenFormat.js";
import { useBubbleSettings } from "../../context/BubbleSettingsContext.js";
import { updateUnseenNotification } from "../../services/api/NotificationsApi.js";
import { useToast } from "../../context/ToastContext";
import draftToHtml from "draftjs-to-html";
import { getHierarchy } from "../../services/api/HierarchyApi.js";
import { useTaskSelection } from "../../context/TaskSelectionContext.js";
import { formatText } from "../../components/TruncateParties.js";

const Tasks = () => {
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const { searchResults, searchType } = useSearchResults();
  const [tasks, setTasks] = useState([]);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskIdToDelete, setTaskIdToDelete] = useState(null);
  const [stats, setStats] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [allLawyers, setAllLawyers] = useState([]);

  // Replace the existing taskTab hook with:
  const {
    taskTab,
    setTaskTab,
    selectedCard,
    handleCardClick,
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    filteredTasks,
    setFilteredTasks,
    isLoading,
    setIsLoading,
    triggerTaskRefresh,
  } = useTaskSelection();

  const [viewMode, setViewMode] = useState("table");

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => { });

  const { bubbleSettings, fetchAndUpdateBubbleSettings } = useBubbleSettings();
  const { showLoading, hideLoading } = useLoading();
  const { showToast } = useToast();
  const [hierarchyUsers, setHierarchyUsers] = useState([]);
  const [userDetailsMap, setUserDetailsMap] = useState({});
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [assignedByOpen, setAssignedByOpen] = useState(false);
  const [assignedToOpen, setAssignedToOpen] = useState(false);
  const [assignedBySearchTerm, setAssignedBySearchTerm] = useState("");
  const [assignedToSearchTerm, setAssignedToSearchTerm] = useState("");

  const handleConfirmDelete = async () => {
    try {
      showLoading();
      await deleteTaskById(taskIdToDelete);
      setTasks((prev) => prev.filter((task) => task._id !== taskIdToDelete));

      if (paginatedTasks.length === 1 && pagination.page > 0) {
        setPagination(prev => ({
          ...prev,
          page: prev.page - 1
        }));
      } else {
        // Otherwise, just refresh the current page's data using the trigger.
        triggerTaskRefresh();
      }
    } catch (err) {
      console.error("Failed to delete task", err);
      showToast("Failed to delete task. Please try again.", "error");
    } finally {
      hideLoading();
      setDeleteModalOpen(false);
    }
  };

  const processSearchResults = (results) => {
    if (!results || !Array.isArray(results)) return [];

    return results.map((task) => ({
      ...task,
      assignedByUser:
        allLawyers.find((lawyer) => lawyer._id === task.assignedBy)?.name ||
        "Unknown",
      assignedToNames: (task.assignedTo || []).map((userId) => {
        const user = allLawyers.find((lawyer) => lawyer._id === userId);
        return user ? user.name : "Unknown";
      }),
    }));
  };

  const isInSearchMode = searchType === "tasks";

  const displayTasks =
    isInSearchMode && searchResults?.length > 0
      ? processSearchResults(searchResults)
      : tasks;

  useEffect(() => {
    fetchLawyers();
  }, []);

  // Initial load
  useEffect(() => {
    if (allLawyers.length > 0) {
      fetchTasks();
    }
  }, [allLawyers]); // Only run on mount and when lawyers are loaded

  // Refetch when filters or pagination changes
  useEffect(() => {
    if (allLawyers.length > 0) {
      fetchTasks();
    }
  }, [taskTab, selectedCard, searchTerm, filters, pagination.page, pagination.limit, triggerTaskRefresh]);

  const fetchLawyers = async () => {
    try {
      const res = await getLawyers();

      // Ensure we have an array of lawyers
      const lawyersData = Array.isArray(res.data) ? res.data : [];
      setAllLawyers(lawyersData);

      // If no lawyers found, log for debugging
      if (lawyersData.length === 0) {
        console.warn("No lawyers found in hierarchy response");
      }
    } catch (err) {
      console.error("Failed to fetch lawyers", err);
      // Set empty array on error
      setAllLawyers([]);
    }
  };

  const fetchTasks = useCallback(async () => {
    try {
      showLoading();
      setTasks([]);
      setFilteredTasks([]);

      await new Promise((resolve) => setTimeout(resolve, 0));

      let response;
      const params = new URLSearchParams();
      if (pagination.limit !== "all") {
        params.set('page', pagination.page.toString());
        params.set('limit', pagination.limit.toString());
      }

      if (searchTerm && searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }

      if (selectedCard) {
        switch (selectedCard) {
          case 'completed':
            params.set('isCompleted', 'true');
            params.set('onClick', 'completed');
            break;
          case 'pending':
            params.set('isCompleted', 'false');
            params.set('onClick', 'pending');
            break;
          case 'today':
            params.set('onClick', 'today');
            break;
          case 'total':
            params.set('onClick', 'total');
            break;
          default:
            break;
        }
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(","));
        } else if (typeof value === "string" && value.trim() && value.trim() !== "all") {
          if (key === "status") {
            params.set("isCompleted", value.trim());
          } else {
            params.set(key, value.trim());
          }
        }
      });

      const queryString = params.toString();

      if (taskTab === "assignedToMe") {
        response = await getTasksAssignedToUser(
          queryString ? `?${queryString}` : ""
        );
      } else {
        response = await getTasksAssignedByUser(
          queryString ? `?${queryString}` : ""
        );
      }

      const { tasks: fetchedTasks = [], ...restStats } = response.data;

      setStats(restStats);

      if (!fetchedTasks || fetchedTasks.length === 0) {
        setTasks([]);
        setFilteredTasks([]);
        updatePaginationInfo(0, false);
        return; 
      }
      const enrichedTasks = await Promise.all(
        fetchedTasks.map(async (task) => {
          let assignedByUser = null;
          try {
            const userRes = await getUserData(task.assignedBy);
            assignedByUser = userRes.data;
          } catch (err) {
            console.error(`Failed to fetch assignedBy user data for task ${task._id}:`, err);
            const fallbackUser = allLawyers.find((lawyer) => lawyer._id === task.assignedBy);
            if (fallbackUser) {
              assignedByUser = { name: fallbackUser.name, _id: fallbackUser._id };
            } else {
              assignedByUser = { name: "Unknown User", _id: task.assignedBy };
            }
          }

          const assignedToNames = (task.assignedTo || []).map((userId) => {
            const user = allLawyers.find((lawyer) => lawyer._id === userId);
            return user ? user.name : "Unknown User";
          });

          return { ...task, assignedByUser, assignedToNames };
        })
      );
      setTasks((prevTasks) => {
        return [...enrichedTasks];
      });

      setFilteredTasks((prevFiltered) => {
        return [...enrichedTasks];
      });

      updatePaginationInfo(
        restStats.totalTasks || enrichedTasks.length,
        pagination.limit === "all" ? false : enrichedTasks.length === pagination.limit
      );

    } catch (err) {
      console.error("FETCH TASKS ERROR:", err);
      setTasks([]);
      setFilteredTasks([]);
      setStats({});
      updatePaginationInfo(0, false);
    } finally {
      hideLoading();
    }
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    selectedCard,
    filters,
    taskTab,
    allLawyers,
    showLoading,
    hideLoading,
    setFilteredTasks,
    updatePaginationInfo,
  ]);


  const toggleTaskCompletion = async (taskId, isCompleted) => {
    try {
      showLoading();
      await updateTaskCompletion(taskId, !isCompleted);
      setTasks((prev) =>
        prev.map((task) =>
          task._id === taskId ? { ...task, isCompleted: !isCompleted } : task
        )
      );
    } catch (err) {
      console.error("Failed to update task completion:", err);
      showToast("Failed to update task completion.", "error");
    } finally {
      hideLoading();
    }
  };

  const handleEditClick = (task) => {
    navigate(`/home/tasks/edit/${task._id}`);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    try {
      showLoading();
      const payload = {
        taskName: editFormData.taskName,
        courtRef: editFormData.courtRef,
        description: editFormData.description,
        dueDate: editFormData.dueDate,
        assignedTo: editFormData.assignedTo,
        document: editFormData.document || "",
      };
      const updated = await updateTask(editFormData._id, payload);

      const updatedAssignedToNames = (updated.data.assignedTo || []).map(
        (userId) => {
          const user = allLawyers.find((lawyer) => lawyer._id === userId);
          return user ? user.name : "Unknown User";
        }
      );

      setTasks((prev) =>
        prev.map((t) =>
          t._id === updated.data._id
            ? {
              ...updated.data,
              assignedByUser: t.assignedByUser,
              assignedToNames: updatedAssignedToNames,
            }
            : t
        )
      );
      setEditModalOpen(false);
    } catch (error) {
      showToast("Error updating task", "error");
      console.error(error);
    } finally {
      hideLoading();
    }
  };

  const handleCancel = () => setEditModalOpen(false);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });

  const handleCourtRefClick = (courtRef) => {
    if (courtRef) {
      navigate(`/home/cases/casedetails/${courtRef}`);
    }
  };

  const getNoDataMessage = () => {
    const hasSearch = searchTerm && searchTerm.trim();
    const hasFilters = hasActiveFilters || selectedCard;

    if (hasSearch && hasFilters) {
      return "No tasks found matching your search and filters. Try adjusting your criteria";
    }

    if (hasSearch) {
      return "No tasks found matching your search. Try different keywords";
    }

    if (hasFilters) {
      return "No tasks found matching the applied filters. Try clearing some filters";
    }

    return taskTab === "assignedToMe"
      ? "No tasks have been assigned to you yet"
      : "You haven't assigned any tasks yet. Create your first task!";
  };

  useEffect(() => {
    const fetchCaseDetails = async (courtRef) => {
      if (!courtRef) return;
      try {
        const response = await getBothCourtCasesDetailsByCourtRef(courtRef);
        setCaseData((prevData) => ({
          ...prevData,
          [courtRef]: response.data,
        }));
      } catch (err) {
        console.error("Error fetching case details:", err);
      } finally {
      }
    };
    tasks.forEach((task) => {
      if (task.courtRef) {
        fetchCaseDetails(task.courtRef);
      }
    });
  }, [tasks]);

  const taskBadgeMap = useMemo(() => {
    const map = {};
    bubbleSettings?.task?.forEach((t) => {
      if (t.source && t.action !== "Task Deleted") {
        map[t.source] =
          t.action === "Task Created"
            ? "New"
            : t.action === "Task Updated"
              ? "Updated"
              : null;
      }
    });
    return map;
  }, [bubbleSettings]);

  const newTaskIds = useMemo(() => {
    return bubbleSettings?.task?.length > 0
      ? bubbleSettings.task.map((t) => t.source)
      : [];
  }, [bubbleSettings]);

  const taskIdToNotificationId = useMemo(() => {
    const map = {};
    bubbleSettings?.task?.forEach((t) => {
      if (t.source && t.id) {
        map[t.source] = t.id;
      }
    });
    return map;
  }, [bubbleSettings]);

  const handleTaskClick = async (task) => {
    const notificationId = taskIdToNotificationId[task._id];

    if (notificationId) {
      try {
        await updateUnseenNotification(notificationId);
        await fetchAndUpdateBubbleSettings(); // Refresh bubble UI immediately
      } catch (err) {
        console.error("Failed to update unseen notification", err);
      }
    }

    navigate(`/home/tasks/viewtask/${task._id}`, {
      state: {
        parties: caseData?.[task.courtRef]?.case?.parties || null,
      },
    });
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState("auto");

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      // Always show below the button, but limit maxHeight if needed
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      // Only close if scroll is outside the dropdown
      const dropdown = document.querySelector(".filter-dropdown-box");
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        // Scrolling inside the dropdown, do nothing
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  // Check if dropdown content is overflowing and set overflowY accordingly
  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow("auto");
      } else {
        setDropdownOverflow("visible");
      }
    }
  }, [isFilterOpen, dropdownPosition]);
  const finalDisplayTasks = useMemo(() => {

    // Just use tasks directly - all filtering happens at API level
    const result = Array.isArray(tasks) ? [...tasks] : [];

    return result;
  }, [tasks]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
      if (!e.target.closest(".checkbox-dropdown")) {
        setAssignedByOpen(false);
        setAssignedToOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchHierarchyUsers = async () => {
    try {
      setLoadingHierarchy(true);
      const hierarchyResponse = await getHierarchy();
      const userIds = hierarchyResponse.data.users || [];
      setHierarchyUsers(userIds);

      // Fetch user details for each ID
      const userDetailsPromises = userIds.map(async (userId) => {
        try {
          const userResponse = await getUserDataByID(userId?._id);
          return { id: userId?._id, data: userResponse.data };
        } catch (err) {
          console.error(`Failed to fetch user data for ID ${userId}:`, err);
          return { id: userId?._id, data: { name: "Unknown User", _id: userId } };
        }
      });

      const userDetailsResults = await Promise.all(userDetailsPromises);
      const detailsMap = {};
      userDetailsResults.forEach(({ id, data }) => {
        detailsMap[id] = data;
      });
      setUserDetailsMap(detailsMap);
    } catch (err) {
      console.error("Failed to fetch hierarchy users:", err);
    } finally {
      setLoadingHierarchy(false);
    }
  };

  const getUserNamesByIds = (userIds) => {
    if (!Array.isArray(userIds) || userIds.length === 0) return "";
    return userIds
      .map((id) => {
        const user = hierarchyUsers.find((user) => user._id === id);
        return user ? user.name : id;
      })
      .join(", ");
  };

  const handleTasksPerPageChange = useCallback((e) => {
    const value = e.target.value;
    const newTasksPerPage = value === "all" ? "all" : Number(value);
    setPagination(prev => ({
      ...prev,
      limit: newTasksPerPage,
      page: 0
    }));
  }, [setPagination]);

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  }, [setPagination]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
  }, [filters, searchTerm, selectedCard, taskTab, setPagination]);

  const paginatedTasks = useMemo(() => {
    return finalDisplayTasks || [];
  }, [finalDisplayTasks]);

  const filteredAssignedByUsers = useMemo(() => {
    return hierarchyUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(assignedBySearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(assignedBySearchTerm.toLowerCase())
    );
  }, [hierarchyUsers, assignedBySearchTerm]);

  const filteredAssignedToUsers = useMemo(() => {
    return hierarchyUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(assignedToSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(assignedToSearchTerm.toLowerCase())
    );
  }, [hierarchyUsers, assignedToSearchTerm]);

  useEffect(() => {
    fetchHierarchyUsers();
  }, []);

  return (
    <div className={`task-container`}>
      <div className="task-header-main">
        <div className="tab-header">
          <button
            className={taskTab === "assignedToMe" ? "tab active" : "tab"}
            onClick={() => setTaskTab("assignedToMe")}
          >
            Assigned to Me
          </button>
          <button
            className={taskTab === "assignedByMe" ? "tab active" : "tab"}
            onClick={() => setTaskTab("assignedByMe")}
          >
            Assigned By Me
          </button>
        </div>
        <div className="invoices-header task-gap-header">
          <div className="view-toggle-wrapper">
            <button
              className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""
                }`}
              onClick={() => setViewMode("grid")}
            >
              <IoGrid />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === "table" ? "active" : ""
                }`}
              onClick={() => setViewMode("table")}
            >
              <FaTableList />
            </button>
            <div className="tasks-page-filter">
              <div className="common-pagination-settings">
                <label htmlFor="tasksPerPageSelect">Tasks per page:</label>
                <select
                  id="tasksPerPageSelect"
                  value={pagination.limit}
                  onChange={handleTasksPerPageChange}
                >
                  {[5, 10, 15, 20, 25, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          </div>
          <div className="filter-dropdown-wrapper">
            <button
              className="filter-toggle-btn"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              ref={filterButtonRef}
            >
              {isFilterOpen ? "Hide Filters" : "Show Filters"}
            </button>

            {isFilterOpen &&
              dropdownPosition.top !== 0 &&
              dropdownPosition.left !== 0 && (
                <div
                  className="filter-dropdown-box"
                  ref={filterDropdownRef}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    maxHeight: dropdownPosition.maxHeight
                      ? `${dropdownPosition.maxHeight}px`
                      : "500px",
                    overflowY: dropdownOverflow,
                    position: "fixed",
                  }}
                >
                  <div className="filter-group">
                    <label>Task Name</label>
                    <input
                      type="text"
                      value={filters.taskName}
                      onChange={(e) =>
                        setFilters({ ...filters, taskName: e.target.value })
                      }
                      placeholder="Enter task name"
                    />
                  </div>
                  <div className="filter-group">
                    <label>Status</label>
                    <select
                      value={filters.status || ""}
                      onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                    >
                      <option value="">All Status</option>
                      <option value="true">Done</option>
                      <option value="false">Pending</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Court Ref</label>
                    <input
                      type="text"
                      value={filters.courtRef}
                      onChange={(e) =>
                        setFilters({ ...filters, courtRef: e.target.value })
                      }
                      placeholder="Enter court reference"
                    />
                  </div>

                  <div className="filter-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) =>
                        setFilters({ ...filters, startDate: e.target.value })
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) =>
                        setFilters({ ...filters, endDate: e.target.value })
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Assigned By</label>
                    <div className="checkbox-dropdown">
                      <div
                        className="checkbox-dropdown-header"
                        onClick={() => setAssignedByOpen(!assignedByOpen)}
                      >
                        <span>
                          {filters.assignedBy.length === 0
                            ? "Select users..."
                            : `${filters.assignedBy.length} selected`}
                        </span>
                        <span
                          className={`dropdown-arrow ${assignedByOpen ? "open" : ""
                            }`}
                        >
                          ▼
                        </span>
                      </div>
                      {assignedByOpen && (
                        <div className="checkbox-dropdown-content">
                          <div
                            style={{
                              padding: "8px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Search users..."
                              value={assignedBySearchTerm}
                              onChange={(e) =>
                                setAssignedBySearchTerm(e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "6px",
                                border: "1px solid #ddd",
                                borderRadius: "3px",
                                fontSize: "14px",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {loadingHierarchy ? (
                            <div className="loading-text">Loading users...</div>
                          ) : (
                            filteredAssignedByUsers.map((user) => (
                              <label key={user._id} className="checkbox-item">
                                <input
                                  type="checkbox"
                                  checked={filters.assignedBy.includes(
                                    user._id
                                  )}
                                  onChange={(e) => {
                                    const userId = user._id;
                                    const newAssignedBy = e.target.checked
                                      ? [...filters.assignedBy, userId]
                                      : filters.assignedBy.filter(
                                        (id) => id !== userId
                                      );
                                    setFilters({
                                      ...filters,
                                      assignedBy: newAssignedBy,
                                    });
                                  }}
                                />
                                <span className="checkmark"></span>
                                <span className="user-name">{user.name}</span>
                              </label>
                            ))
                          )}
                          {filteredAssignedByUsers.length === 0 && assignedBySearchTerm && !loadingHierarchy && (
                            <div
                              style={{
                                padding: "8px",
                                color: "#999",
                                fontStyle: "italic",
                              }}
                            >
                              No users found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="filter-group">
                    <label>Assigned To</label>
                    <div className="checkbox-dropdown">
                      <div
                        className="checkbox-dropdown-header"
                        onClick={() => setAssignedToOpen(!assignedToOpen)}
                      >
                        <span>
                          {filters.assignedTo.length === 0
                            ? "Select users..."
                            : `${filters.assignedTo.length} selected`}
                        </span>
                        <span
                          className={`dropdown-arrow ${assignedToOpen ? "open" : ""
                            }`}
                        >
                          ▼
                        </span>
                      </div>
                      {assignedToOpen && (
                        <div className="checkbox-dropdown-content">
                          <div
                            style={{
                              padding: "8px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Search users..."
                              value={assignedToSearchTerm}
                              onChange={(e) =>
                                setAssignedToSearchTerm(e.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "6px",
                                border: "1px solid #ddd",
                                borderRadius: "3px",
                                fontSize: "14px",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {loadingHierarchy ? (
                            <div className="loading-text">Loading users...</div>
                          ) : (
                            filteredAssignedToUsers.map((user) => (
                              <label key={user._id} className="checkbox-item">
                                <input
                                  type="checkbox"
                                  checked={filters.assignedTo.includes(
                                    user._id
                                  )}
                                  onChange={(e) => {
                                    const userId = user._id;
                                    const newAssignedTo = e.target.checked
                                      ? [...filters.assignedTo, userId]
                                      : filters.assignedTo.filter(
                                        (id) => id !== userId
                                      );
                                    setFilters({
                                      ...filters,
                                      assignedTo: newAssignedTo,
                                    });
                                  }}
                                />
                                <span className="checkmark"></span>
                                <span className="user-name">{user.name}</span>
                              </label>
                            ))
                          )}
                          {filteredAssignedToUsers.length === 0 && assignedToSearchTerm && !loadingHierarchy && (
                            <div
                              style={{
                                padding: "8px",
                                color: "#999",
                                fontStyle: "italic",
                              }}
                            >
                              No users found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="filter-actions">
                    <button className="clear-btn" onClick={clearAllFilters}>
                      Clear All
                    </button>
                    <button
                      className="hide-btn"
                      onClick={() => setIsFilterOpen(false)}
                    >
                      Hide
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {(hasActiveFilters || selectedCard || (searchTerm && searchTerm.trim())) && (
        <div className="applied-filters">
          <span className="filters-text">Applied Filters:</span>

          {/* Search term filter pill */}
          {searchTerm && searchTerm.trim() && (
            <span
              className="filter-tag"
              onClick={() => {
                setSearchTerm('');
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Search: {formatText(searchTerm)} ×
            </span>
          )}

          {/* Card selection filter pill */}
          {selectedCard && (
            <span
              className="filter-tag"
              onClick={() => handleCardClick(selectedCard)}
            >
              Card: {formatText(selectedCard)} ×
            </span>
          )}

          {/* Individual filter pills */}
          {filters.taskName && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, taskName: '' })}
            >
              Task Name: {formatText(filters.taskName)} ×
            </span>
          )}
          {filters.status && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, status: '' })}
            >
              Status: {filters.status === 'true' ? 'Done' : 'Pending'} ×
            </span>
          )}

          {filters.courtRef && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, courtRef: '' })}
            >
              Court Ref: {formatText(filters.courtRef)} ×
            </span>
          )}

          {filters.startDate && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, startDate: '' })}
            >
              Start Date: {formatText(filters.startDate)} ×
            </span>
          )}

          {filters.endDate && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, endDate: '' })}
            >
              End Date: {formatText(filters.endDate)} ×
            </span>
          )}

          {filters.assignedBy.length > 0 && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, assignedBy: [] })}
            >
              Assigned By: {formatText(getUserNamesByIds(filters.assignedBy))} ×
            </span>
          )}

          {filters.assignedTo.length > 0 && (
            <span
              className="filter-tag"
              onClick={() => setFilters({ ...filters, assignedTo: [] })}
            >
              Assigned To: {formatText(getUserNamesByIds(filters.assignedTo))} ×
            </span>
          )}

          {/* Clear All button */}
          <span
            className="filter-tag clear-all-tag"
            onClick={() => {
              clearAllFilters();
              // Clear DetailSection's search input  
              if (window.clearDetailSectionSearch) {
                window.clearDetailSectionSearch();
              }
            }}
          >
            Clear All
          </span>
        </div>
      )}


      <div className="task-grid">
        {paginatedTasks.length === 0 ? (
          <p className="no-tasks">{getNoDataMessage()}</p>
        ) : viewMode === "grid" ? (
          paginatedTasks.map((task) => (
            <div
              key={task._id}
              className="task-card-main"
              onClick={() => handleTaskClick(task)}
              style={{ cursor: "pointer", position: "relative" }}
            >
              {taskBadgeMap[task._id] && (
                <span
                  className={`badge ${taskBadgeMap[
                    task._id
                  ].toLowerCase()}-badge`}
                >
                  {taskBadgeMap[task._id]}
                </span>
              )}

              <div className="task-card-header">
                <div className="task-title-group">
                  <h3 className="task-title">{task.taskName}</h3>
                </div>
                <div className="task-card-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmationOpen(true);
                      setConfirmAction(
                        () => () =>
                          toggleTaskCompletion(task._id, task.isCompleted)
                      );
                    }}
                    className={`action-button status-btn ${task.isCompleted ? "done-btn" : "pending-btn"
                      }`}
                    aria-label={
                      task.isCompleted ? "Mark as pending" : "Mark as done"
                    }
                  >
                    {task.isCompleted ? "Done" : "Pending"}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(task);
                    }}
                    className="action-button edit-btn"
                    aria-label="Edit task"
                  >
                    <BiEdit size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTaskIdToDelete(task._id);
                      setDeleteModalOpen(true);
                    }}
                    className="action-button delete-btn"
                    aria-label="Delete task"
                  >
                    <BiTrash size={16} />
                  </button>
                </div>
              </div>
              {task.courtRef && (
                <div
                  className="task-courtRef-tag"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCourtRefClick(task.courtRef);
                  }}
                >
                  {caseData && caseData[task.courtRef]?.case?.parties
                    ? WrittenFormat(caseData[task.courtRef].case.parties)
                    : "Parties not available"}
                  ({task.courtRef})
                </div>
              )}
              <div className="task-details-grid">
                <span className="detail-item">
                  <BiCalendar className="detail-icon" />
                  <span className="detail-label">Due Date:</span>{" "}
                  <span className="detail-value">
                    {formatDate(task.dueDate)}
                  </span>
                </span>
                <span className="detail-item">
                  <BiTime className="detail-icon" />
                  <span className="detail-label">Created At:</span>{" "}
                  <span className="detail-value">
                    {formatDate(task.createdAt)}
                  </span>
                </span>
                {task.assignedByUser && (
                  <span className="detail-item">
                    <BiUser className="detail-icon" />
                    <span className="detail-label">Assigned By:</span>{" "}
                    <span className="detail-value">
                      {formatText(task.assignedByUser.name)}
                    </span>
                  </span>
                )}
                {task.assignedToNames && task.assignedToNames.length > 0 && (
                  <span className="detail-item full-width">
                    <BiUser className="detail-icon" />
                    <span className="detail-label">Assigned To:</span>{" "}
                    <span className="detail-value assigned-to-list">
                      {task.assignedToNames.join(", ")}
                    </span>
                  </span>
                )}
              </div>

              {task.description && (
                <div className="task-description">
                  <BiDetail className="description-icon" />
                  <p>
                    <strong>Description:</strong>
                    {task.description ? (
                      (() => {
                        let html = "";
                        try {
                          const raw =
                            typeof task.description === "string"
                              ? JSON.parse(task.description)
                              : task.description;
                          if (raw && raw.blocks) {
                            html = draftToHtml(raw);
                          } else {
                            html = task.description;
                          }
                        } catch (e) {
                          html = task.description;
                        }
                        // Fix anchor tags to always use absolute URLs
                        let fixedHtml = html.replace(
                          /<a ([^>]*href=["'])(?!https?:\/\/)([^"'>]+)(["'][^>]*)>/gi,
                          "<a $1https://$2$3>"
                        );

                        fixedHtml = fixedHtml.replace(
                          /<a /gi,
                          '<a target="_blank" rel="noopener noreferrer" '
                        );
                        return (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: fixedHtml,
                            }}
                          />
                        );
                      })()
                    ) : (
                      <p>No description provided</p>
                    )}
                  </p>
                </div>
              )}

              {task.document && (
                <div className="task-document">
                  <BiLink className="document-icon" />
                  <a
                    href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${task.document}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Document
                  </a>
                </div>
              )}
            </div>
          ))
        ) : (
          <table className="tasks-table">
            <thead>
              <tr>
                <th>Task Name</th>
                <th>Due Date</th>
                <th>Court Ref</th>
                <th>Assigned By</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTasks.map((task) => (
                <tr
                  key={task._id}
                  onClick={() => handleTaskClick(task)}
                  style={{ cursor: "pointer", position: "relative" }}
                >
                  <td>
                    <div
                      className="table-task-name"
                      onClick={() => handleTaskClick(task)}
                    >
                      <span className="task-name-text">{formatText(task.taskName)}</span>
                    </div>
                  </td>
                  <td>{formatDate(task.dueDate)}</td>

                  <td>
                    {task.courtRef ? (
                      <span
                        className="table-court-ref"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCourtRefClick(task.courtRef);
                        }}
                        title={
                          caseData?.[task.courtRef]?.case?.parties
                            ? WrittenFormat(
                              caseData[task.courtRef].case.parties
                            )
                            : "Parties not available"
                        }
                      >
                        {task.courtRef}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{formatText(task.assignedByUser?.name) || "—"}</td>
                  <td>{task.assignedToNames?.join(", ") || "—"}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(task);
                        }}
                        className="table-action-btn edit"
                        title="Edit task"
                      >
                        <BiEdit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTaskIdToDelete(task._id);
                          setDeleteModalOpen(true);
                        }}
                        className="table-action-btn delete"
                        title="Delete task"
                      >
                        <BiTrash size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmationOpen(true);
                          setConfirmAction(
                            () => () =>
                              toggleTaskCompletion(task._id, task.isCompleted)
                          );
                        }}
                        className={`table-action-btn status-btn ${task.isCompleted ? "done-btn" : "pending-btn"
                          }`}
                        title={
                          task.isCompleted ? "Mark as pending" : "Mark as done"
                        }
                      >
                        {task.isCompleted ? "Done" : "Pending"}
                      </button>
                      {taskBadgeMap[task._id] && (
                        <span
                          className={`badge ${taskBadgeMap[
                            task._id
                          ].toLowerCase()}-badge table-badge`}
                        >
                          {taskBadgeMap[task._id]}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {pagination.limit !== "all" && (pagination.totalTasks > pagination.limit || pagination.page > 0) && (
        <div className="pagination-controls">
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(0)}
          >
            First
          </button>
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page + 1} of {Math.ceil(pagination.totalTasks / pagination.limit)}
          </span>
          <button
            disabled={pagination.page >= Math.ceil(pagination.totalTasks / pagination.limit) - 1}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
          <button
            disabled={pagination.page >= Math.ceil(pagination.totalTasks / pagination.limit) - 1}
            onClick={() => handlePageChange(Math.ceil(pagination.totalTasks / pagination.limit) - 1)}
          >
            Last
          </button>
        </div>
      )}

      {editModalOpen && editFormData && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <h2>Edit Task</h2>

            <input
              type="text"
              name="taskName"
              value={editFormData.taskName}
              onChange={handleEditChange}
            />
            <input
              type="text"
              name="courtRef"
              value={editFormData.courtRef}
              onChange={handleEditChange}
            />
            <textarea
              name="description"
              value={editFormData.description}
              onChange={handleEditChange}
            />
            <input
              type="date"
              name="dueDate"
              value={editFormData.dueDate?.split("T")[0]}
              onChange={handleEditChange}
            />

            <div className="modal-actions">
              <button onClick={handleEditSubmit} className="update-btn">
                Update
              </button>
              <button onClick={handleCancel} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        itemType="this task"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
      <ConfirmationModal
        isOpen={confirmationOpen}
        message="Are you sure you want to update the task completion status?"
        onConfirm={() => {
          confirmAction();
          setConfirmationOpen(false);
        }}
        onCancel={() => setConfirmationOpen(false)}
      />
    </div>
  );
};

export default Tasks;